import { Category1Types } from 'enums/category1Enums';
import {
  Origins,
  ScopeThreeCategoryOne,
} from 'models/ScopeThreeCategoryOne/ScopeThreeCategoryOne.model';
import React, { FC, useEffect } from 'react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Origin } from 'views/CreateProductFlow/CreateProductFlowInterfaces';
import { categoryTypeFieldMap } from 'views/Scope3/Category4&9/Category4Layout/category4LayoutData';
import FindLocationModal from '../FindLocationModal';
import { InputFieldProps } from '../InputField';
import RHFInputField from '../ReactHookFormFields/RHFInputField';
import { generateAddressLabel } from 'views/Scope3/Category4&9/LocationModal';
import { addOriginHandler } from 'shared/utils/handleIngredientChange';

export interface LocationModalInputFieldProps extends InputFieldProps {
  type: Category1Types;
  index: number;
  selectedOriginIndex: number;
  onSave?: (data: Origins[]) => void;
}

const LocationModalInputField: FC<LocationModalInputFieldProps> = ({
  type,
  index,
  selectedOriginIndex,
  onSave,
  ...fieldProps
}) => {
  const { watch, setValue } = useFormContext<ScopeThreeCategoryOne>();
  const [selectedIndex, setSelectedIndex] = useState<number>();
  const handleClick = () => setSelectedIndex(index);
  const typeValue = categoryTypeFieldMap[type];
  const activeOrigins = watch(`${typeValue}.${index}.origins`);
  const isMultipleOrigin = watch(`${typeValue}.${index}.multiOrigin`);
  const firstBsetFitRegion = watch(
    `${typeValue}.${index}.activityMappingRegionSpecId`
  );
  useEffect(() => {
    if (!isMultipleOrigin) {
      const desiredAddress = watch(`${typeValue}.${index}.address`);
      const originIndexAddress = watch(
        `${typeValue}.${index}.origins.0.address`
      );
      setValue(
        `${typeValue}.${index}.origins.0.address`,
        desiredAddress ? desiredAddress : originIndexAddress
      );
      firstBsetFitRegion &&
        setValue(
          `${typeValue}.${index}.origins.0.activityMappingRegionSpecId`,
          watch(`${typeValue}.${index}.activityMappingRegionSpecId`)
        );
    }
  }, [isMultipleOrigin]);
  const handleSave = (data: Origins[]) => {
    if (onSave) {
      onSave?.(data);
      return;
    }
    const isSingleOrigin = data.length === 1;
    const firstOrigin = data[0];
    setValue(`${typeValue}.${index}.origins`, data as Origins[]);
    setValue(`${typeValue}.${index}.bestFitRegion`, undefined);
    if (isSingleOrigin) {
      firstOrigin.share = null;
      const label = firstOrigin?.address
        ? generateAddressLabel(firstOrigin?.address)
        : '';
      setValue(`${typeValue}.${index}.addressLabel`, label, {
        shouldValidate: true,
      });
      setValue(`${typeValue}.${index}.address`, firstOrigin?.address, {
        shouldValidate: true,
      });
      setValue(
        `${typeValue}.${index}.activityMappingRegionSpecId`,
        firstOrigin?.activityMappingRegionSpecId ||
          watch(`${typeValue}.${index}.activityMappingRegionSpecId`),
        {
          shouldValidate: true,
        }
      );
      setValue(`${typeValue}.${index}.multiOrigin`, false, {
        shouldValidate: true,
      });
    }
    data.forEach((origin, originIndex) => {
      const label = origin?.address ? generateAddressLabel(origin.address) : '';
      setValue(
        `${typeValue}.${index}.origins.${originIndex}.addressLabel`,
        label,
        { shouldValidate: true }
      );
      setValue(
        `${typeValue}.${index}.origins.${originIndex}.activityMappingRegionSpecId`,
        origin.activityMappingRegionSpecId ?? '',
        { shouldValidate: true }
      );
    });
    setSelectedIndex(undefined);
  };

  const handleClose = () => setSelectedIndex(undefined);
  const handleAddOrigin = () => {
    if (watch(`${typeValue}.${index}.origins`).length === 1) {
      addOriginHandler(typeValue, index, watch, setValue);
    }
    setValue(`${typeValue}.${index}.multiOrigin`, true);
  };

  return (
    <div>
      <RHFInputField
        {...(fieldProps as InputFieldProps)}
        onClick={handleClick}
        readOnly
      />
      <FindLocationModal
        showModal={selectedIndex !== undefined}
        selectedIndexData={activeOrigins as Origin[]}
        onSave={handleSave}
        onCancel={handleClose}
        activeTabIndex={selectedOriginIndex}
        addCallBack={handleAddOrigin}
      />
    </div>
  );
};

export default LocationModalInputField;
